'use client'

import { Button, Cell, Container, InputText, Spacer, Text, Validation } from '@vinted/web-ui'
import { camelCase } from 'lodash'
import { useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'

import { ErrorItem, HttpStatus } from '@marketplace-web/core-api/api-client-util'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import {
  renderValidation,
  useFormValidationMessage,
} from '@marketplace-web/vendor-abstractions/react-hook-form-ui'
import { isValueInObject } from '@marketplace-web/shared/utils'
import { updateEprUin } from '@marketplace-web/domain/business-accounts-data'

type Props = {
  onClose: () => void
  uinNumber: string
  uinId: number
  refetch: () => void
}

type EditCountryUinFormData = {
  uin: string
}

enum EditUinField {
  Uin = 'uin',
}

const EditCountryUinForm = ({ onClose, uinNumber, uinId, refetch }: Props) => {
  const [showGeneralError, setShowGeneralError] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const handleFormCancel = () => onClose()

  const formMethods = useForm<EditCountryUinFormData>({
    defaultValues: {
      uin: uinNumber,
    },
  })

  const {
    register,
    setError,
    formState: { errors },
  } = formMethods

  const setBackendErrors = (fieldErrors: Array<ErrorItem>) => {
    fieldErrors.forEach(({ field, value }) => {
      const fieldName = camelCase(field)

      if (isValueInObject(fieldName, EditUinField)) {
        setError(fieldName, { type: 'manual', message: value })
      }
    })
  }

  const handleFormSubmit = async (
    event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>,
  ) => {
    if (isLoading) {
      event.preventDefault()

      return
    }

    setShowGeneralError(false)
    setIsLoading(true)

    const response = await updateEprUin({ uinId, uinNumber: formMethods.getValues().uin })

    if ('errors' in response) {
      setBackendErrors(response.errors)

      if (
        response.status === HttpStatus.InternalError ||
        response.status === HttpStatus.Unauthorized
      ) {
        setShowGeneralError(true)
      }

      setIsLoading(false)

      return
    }

    if (response.message_code === 'ok') {
      setIsLoading(false)

      refetch()

      onClose()
    }
  }

  const baseTranslate = useTranslate('epr_settings.edit_uin')
  const errorTranslate = useTranslate('error_state')
  const validationMessage = useFormValidationMessage(errors, 'epr_settings.edit_uin')

  const renderEditUinFormContent = () => {
    return (
      <FormProvider {...formMethods}>
        <Cell>
          <InputText
            {...register(EditUinField.Uin, {
              required: true,
            })}
            styling={InputText.Styling.Tight}
            title={baseTranslate('uin.title')}
            placeholder={baseTranslate('uin.placeholder')}
            defaultValue={uinNumber}
            validation={renderValidation(validationMessage(EditUinField.Uin))}
          />
        </Cell>
        <div className="u-ui-padding-top-x-large u-ui-padding-bottom-medium u-ui-padding-left-large">
          <Text as="h4" type={Text.Type.Caption} text={baseTranslate('security_disclaimer')} />
        </div>
        {showGeneralError && <Validation text={errorTranslate('title')} theme="warning" />}
        <Spacer size={Spacer.Size.Small} />
        <Container>
          <Button
            text={baseTranslate('actions.submit')}
            onClick={handleFormSubmit}
            styling={Button.Styling.Filled}
            isLoading={isLoading}
          />
          <Spacer size={Spacer.Size.Medium} />
          <Button
            text={baseTranslate('actions.cancel')}
            onClick={handleFormCancel}
            styling={Button.Styling.Flat}
          />
        </Container>
      </FormProvider>
    )
  }

  return renderEditUinFormContent()
}

export default EditCountryUinForm
